/**
 * SfxProviderSelector — thin wrapper around ProviderPicker.
 *
 * Maps cloud + local SFX providers to PickerOption[], groups by T2A / V2A,
 * and delegates layout / sort / collapsed-preview to ProviderPicker.
 *
 * Exported types (SfxProvider, SfxProviderInfo, SfxProviderType) and
 * SFX_PROVIDER_OPTIONS are preserved for SfxDurationSlider + SfxGenerationPanel.
 */

import { memo, useMemo } from 'react';
import { Music, Check, HardDrive, Download, Monitor } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { isDesktopApp, isServerlessMode } from '../../utils/apiMode';
import { applyLocalDiscount, LOCAL_GPU_PRICE_MULTIPLIER } from '../../utils/localPricing';
import { useLocalModelInstalls } from '../../hooks/useLocalModelInstalls';
import {
  SFX_COMPONENT_IDS,
  SFX_LOCAL_PROVIDERS,
  getSfxProviderLimits,
  getSfxQualityRating,
} from '../../constants/soundEffects';
import { ProviderPicker } from '../common/ProviderPicker';
import type { PickerOption } from '../common/ProviderPicker';
import {
  SFX_PROVIDER_OPTIONS,
  type SfxProvider,
  type SfxProviderType,
} from './SfxProviderSelector.constants';

// ============================================================================
// Component
// ============================================================================

interface SfxProviderSelectorProps {
  value: SfxProvider;
  onChange: (provider: SfxProvider) => void;
  disabled?: boolean;
  className?: string;
  /** Set of provider IDs enabled on the backend. If null, availability hasn't been fetched yet. */
  enabledProviders?: Set<string> | null;
}

type SfxMeta = {
  typeGroup: string;
  isLocal: boolean;
  componentId?: string;
};

export const SfxProviderSelector = memo(function SfxProviderSelector({
  value,
  onChange,
  disabled = false,
  className = '',
  enabledProviders = null,
}: SfxProviderSelectorProps) {
  const { t } = useTranslation();
  const isDesktop = isDesktopApp();
  const isCloud = isServerlessMode();
  const { installedModels, installProgress, modelRequirements } =
    useLocalModelInstalls(SFX_COMPONENT_IDS);

  const options = useMemo<PickerOption<SfxMeta>[]>(() => {
    const allOptions: PickerOption<SfxMeta>[] = [];

    // ---- Local providers (desktop-only) ----
    if (isDesktop) {
      Object.entries(SFX_LOCAL_PROVIDERS).forEach(([id, info]) => {
        const type: SfxProviderType = getSfxProviderLimits(id).requiresVideo ? 'V2A' : 'T2A';
        const cloudEquivalent = SFX_PROVIDER_OPTIONS.find((p) => p.value === info.cloudEquivalent);
        const localPricing = cloudEquivalent
          ? applyLocalDiscount(cloudEquivalent.pricing, LOCAL_GPU_PRICE_MULTIPLIER)
          : 'Free';
        // info already has componentId — no need for getLocalProviderInfo
        const componentId = info.componentId;
        const progress = installProgress[componentId];
        const isInstalling =
          progress && progress.stage !== 'complete' && progress.stage !== 'error';
        // Hardware verdict from the backend (GET /api/v1/system/hardware).
        // hunyuan_foley_local and ltx2_audio_local are CUDA-only, so offering
        // them on an Apple-Silicon Mac just sends the user through a 38GB
        // download that can never run.
        const requirements = modelRequirements[componentId];
        const isIncompatible = requirements?.compatible === false;

        allOptions.push({
          value: id,
          label: info.name,
          description: info.description,
          pricing: 'Free',
          pricingNumeric: 0,
          quality: getSfxQualityRating(id) as 1 | 2 | 3 | 4 | 5,
          disabled: isInstalling === true || isIncompatible,
          disabledReason: isIncompatible
            ? t('soundEffects.hardwareNotSupported', {
                defaultValue: 'This machine does not meet the requirements ({{vram}}GB VRAM)',
                vram: requirements?.vramGb ?? 0,
              })
            : undefined,
          meta: {
            typeGroup: `${type} (Local)`,
            isLocal: true,
            componentId,
          },
        } satisfies PickerOption<SfxMeta>);

        void localPricing;
      });
    }

    // ---- Cloud providers ----
    SFX_PROVIDER_OPTIONS.forEach((opt) => {
      const isCloudDisabled = enabledProviders !== null && !enabledProviders.has(opt.value);

      allOptions.push({
        value: opt.value,
        label: opt.label,
        description: opt.description,
        pricing: opt.pricing,
        quality: opt.quality as 1 | 2 | 3 | 4 | 5,
        recommended: opt.value === 'audiocraft',
        disabled: isCloudDisabled,
        disabledReason: isCloudDisabled
          ? t('soundEffects.freeDesktopOnly', { defaultValue: 'Free, Desktop Only' })
          : undefined,
        meta: {
          typeGroup: opt.type === 'V2A' ? 'V2A (Cloud)' : 'T2A (Cloud)',
          isLocal: false,
        },
      } satisfies PickerOption<SfxMeta>);
    });

    return allOptions;
  }, [isDesktop, installProgress, modelRequirements, enabledProviders, t]);

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label flex items-center gap-2 mb-1">
        <Music className="h-4 w-4 flex-shrink-0" />
        <span>
          {t('soundEffects.providerSelector.title', { defaultValue: 'Sound Effect Provider' })}
        </span>
      </label>
      <p className="text-xs text-white/50 mb-2">
        {t('soundEffects.providerSelector.description', {
          defaultValue: 'Choose your AI sound effect generation provider',
        })}
      </p>

      <ProviderPicker<SfxMeta>
        options={options}
        value={value}
        onChange={(v) => onChange(v as SfxProvider)}
        sortKeys={['price', 'quality', 'name']}
        defaultSortKey="quality"
        disabled={disabled}
        groupBy={(opt) => opt.meta?.typeGroup ?? null}
        renderCardExtras={(opt) => {
          // Local provider extras: install status, VRAM, download progress
          if (opt.meta?.isLocal && opt.meta.componentId) {
            const componentId = opt.meta.componentId;
            const isInstalled = installedModels.has(componentId);
            const progress = installProgress[componentId];
            const isInstalling =
              progress && progress.stage !== 'complete' && progress.stage !== 'error';
            // Requirements come from the backend component registry (which
            // derives them from constants/components/sound_effects.py). The
            // hardcoded strings this used to render were 3x too optimistic —
            // LTX-2 Audio was advertised as "8GB+ / 4GB" for a model that
            // needs 24GB of CUDA VRAM and a 38GB download.
            const requirements = modelRequirements[componentId];

            return (
              <div className="space-y-1 mt-1">
                {isInstalled ? (
                  <div className="flex items-center gap-1 text-xs text-green-400">
                    <Check className="w-3 h-3" />
                    <span>{t('common.installed', { defaultValue: 'Installed' })}</span>
                  </div>
                ) : requirements ? (
                  <div className="text-xs text-white/40">
                    <div className="flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      <span>{`${requirements.vramGb}GB+ VRAM`}</span>
                    </div>
                    <div className="text-[10px]">
                      {`${(requirements.downloadMb / 1024).toFixed(1)}GB download`}
                    </div>
                  </div>
                ) : null}
                {isInstalling && progress && (
                  <div className="flex items-center gap-1 text-xs text-blue-400">
                    <Download className="w-3 h-3 animate-pulse" />
                    <span>{Math.round(progress.percent)}%</span>
                  </div>
                )}
              </div>
            );
          }

          // Cloud provider in serverless mode: "Free on Desktop" hint
          if (!opt.meta?.isLocal && isCloud && !opt.disabled) {
            return (
              <div
                className="flex items-center gap-1 mt-1 cursor-pointer group/desktop"
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
                }}
              >
                <Monitor className="w-3 h-3 text-emerald-400/60 group-hover/desktop:text-emerald-400 transition-colors" />
                <span className="text-[10px] text-emerald-400/60 font-medium group-hover/desktop:text-emerald-400 transition-colors">
                  {t('soundEffects.freeOnDesktop', { defaultValue: 'Free on Desktop' })}
                </span>
              </div>
            );
          }

          return null;
        }}
      />

      <div className="mt-3 p-3 rounded-lg bg-blue-900/20 border border-blue-500/30">
        <p className="text-xs text-blue-300">
          {t('soundEffects.providerSelector.info', {
            defaultValue: 'T2A generates audio from text prompts. V2A synchronizes audio to video.',
          })}
        </p>
      </div>
    </div>
  );
});

export default SfxProviderSelector;
